import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Center, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { Questionnaire } from "src/graphql/generated/graphql";
import { useCampaignApplications } from "src/hooks/campaignApplications";
import { CampaignQueryDataType, useCampaignProductReviewDestinations } from "src/hooks/campaigns";

import { ProductSelector } from "./components/ProductSelector";
import { SectionGroup } from "./components/SectionGroup";
import { deriveEshopBarsHybrid } from "./derive";
import { useReportReviews } from "./reportReviews";
import { useCampaignReviewDeliveries } from "./reviewDeliveryCounts";
import { ReportEvaluation } from "./sections/evaluation";
import { ReportFeedback } from "./sections/feedback";
import { ReportFunnel } from "./sections/funnel";
import { ReportOutcome } from "./sections/outcome";
import { ReportQuestionnaire } from "./sections/questionnaire";
import { ReportWhatTested } from "./sections/whatTested";

export interface ReportTabProps {
    campaign: CampaignQueryDataType;
    questionnaire?: Questionnaire;
}

export const ReportTab = ({ campaign, questionnaire }: ReportTabProps) => {
    const { t } = useTranslation(["common"]);
    const [searchParams, setSearchParams] = useSearchParams();
    const productId = searchParams.get("productId");

    // PDF export: rasterise the whole report node to a single, page-break-free PDF and download it
    // directly (no native print dialog). While exporting, `isExporting` expands the feedback groups
    // and shows the campaign-name header so both are captured.
    const reportRef = useRef<HTMLDivElement>(null);
    const [isExporting, setIsExporting] = useState(false);

    // Questionnaire comes from the applications REST API (pagination + protobuf decoding handled
    // inside the hook). It only fires once the Report tab is mounted.
    // silent: transient fetch errors here shouldn't pop a toast — the tab shows one loader and
    // then whatever data arrived (the report degrades gracefully on a partial failure).
    const { allCampaignApplications, isLoadingCampaignApplications } = useCampaignApplications(
        campaign?.id ?? "",
        false,
        true
    );
    const { productReviewDestinations, productReviewTranslationDestinations, isLoadingProductReviewDestinations } =
        useCampaignProductReviewDestinations(campaign?.id ?? "", true);

    const products = useMemo(() => campaign?.products || [], [campaign?.products]);
    const selectedProductId = productId || products[0]?.id;

    // The report is driven entirely by the evaluated campaign.results (see productResultsList
    // below). Reviews are still fetched, but only to feed the e-shop funnel bars and the
    // pros/cons feedback — never to recompute results. The Report tab is gated on evaluation,
    // so this only runs on evaluated campaigns (which have approved reviews).
    const { reviews, isLoadingReviews } = useReportReviews(campaign);
    // E-shop funnel bars use the live shipped copies (real shipped signal, with each copy's lang and
    // origin so translations get their own bars), independent of the per-review fetch above (which
    // now only feeds the feedback section).
    const { deliveries, isLoadingDeliveries } = useCampaignReviewDeliveries(campaign?.id ?? "");

    const productResultsList = useMemo(() => campaign?.results?.products || [], [campaign?.results?.products]);
    const productResults = useMemo(
        () => productResultsList.find((item) => item?.productId === selectedProductId) || productResultsList[0],
        [productResultsList, selectedProductId]
    );
    // Integrated portals counted by delivered (isShipped) count; custom (no-integration) sources
    // counted from submittedContents (the review-submitted-here signal). One bar per destination ×
    // (main | translation tenant), each with its products' URLs.
    const eshopBars = useMemo(
        () =>
            deriveEshopBarsHybrid(
                productReviewDestinations,
                productReviewTranslationDestinations,
                deliveries,
                reviews,
                products
            ),
        [productReviewDestinations, productReviewTranslationDestinations, deliveries, reviews, products]
    );
    // Feedback (likes/dislikes) is per selected product.
    const feedbackReviews = useMemo(
        () => reviews.filter((review) => (review.product?.id || "") === selectedProductId),
        [reviews, selectedProductId]
    );

    const selectProduct = (id: string) =>
        setSearchParams({ ...Object.fromEntries(searchParams.entries()), productId: id });

    // The tab is disabled until evaluation; this guards direct-URL access to an un-evaluated
    // campaign's report.
    if (productResultsList.length === 0) {
        return (
            <Paper withBorder radius={16} p={24}>
                <Text color="dimmed">{t("campaign_report_not_evaluated")}</Text>
            </Paper>
        );
    }

    // Hold the whole tab behind a single loader until every section's data has arrived, so
    // sections don't pop in one-by-one and mid-load fetch races stay invisible.
    const isReportLoading =
        isLoadingReviews || isLoadingDeliveries || isLoadingProductReviewDestinations || isLoadingCampaignApplications;
    if (isReportLoading) {
        return (
            <Center mih={360}>
                <Loader />
            </Center>
        );
    }

    const handleExportPdf = async () => {
        const node = reportRef.current;
        if (!node) {
            return;
        }
        setIsExporting(true);
        try {
            // Let the export-only header + expanded feedback commit + paint before capturing.
            await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
            // html-to-image (SVG foreignObject) rather than html2canvas: it delegates rendering to
            // the browser, so it captures the NPS gauge — a conic-gradient clipped by
            // `clip-path: url(#…)` — which html2canvas re-implements CSS and cannot draw.
            const [{ toCanvas }, { jsPDF }] = await Promise.all([import("html-to-image"), import("jspdf")]);
            const canvas = await toCanvas(node, {
                pixelRatio: 2,
                backgroundColor: "#ffffff",
                // Exclude the export button (and its subtree) from the capture.
                filter: (element) => !(element instanceof HTMLElement && element.classList.contains("report-no-print")),
            });
            const width = node.offsetWidth;
            const height = node.offsetHeight;
            const padding = 40; // white margin around the whole report in the PDF
            const pageWidth = width + padding * 2;
            const pageHeight = height + padding * 2;
            // One page sized to the whole report + margin — a seamless tab with no A4 page breaks.
            const pdf = new jsPDF({
                orientation: pageWidth >= pageHeight ? "landscape" : "portrait",
                unit: "px",
                format: [pageWidth, pageHeight],
                hotfixes: ["px_scaling"],
            });
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, pageHeight, "F");
            // JPEG (not PNG): the report is flat colours + gradients, which JPEG compresses to a
            // few MB instead of tens of MB, with no visible quality loss at scale 2.
            pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", padding, padding, width, height);
            pdf.save(`report-${campaign?.slug || campaign?.id || "campaign"}.pdf`);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box ref={reportRef} id="report-print">
            {/* Download the whole report as one page-break-free PDF (client-side, no print dialog). */}
            <Group position="right" mb="md" className="report-no-print">
                <Button
                    variant="light"
                    leftIcon={<IconDownload size={16} />}
                    onClick={handleExportPdf}
                    loading={isExporting}
                >
                    {t("campaign_report_download_pdf")}
                </Button>
            </Group>
            {/* Rendered only during export — gives the PDF a campaign title for context. */}
            {isExporting && (
                <Text size={22} weight={700} color="#0F1B3D" mb={24}>
                    {campaign?.name}
                </Text>
            )}
            <Stack spacing={48}>
                {/* Campaign-wide data: identical regardless of product. */}
                <SectionGroup title={t("campaign_report_section_campaign")} accent="#7A8699">
                    <ReportWhatTested campaign={campaign} />
                    <ReportFunnel campaign={campaign} results={productResultsList} eshopBars={eshopBars} />
                    <ReportQuestionnaire
                        questionnaire={questionnaire}
                        applications={allCampaignApplications}
                        isLoading={isLoadingCampaignApplications}
                    />
                </SectionGroup>

                {/* Per-product data: driven by the product selector / ?productId. */}
                <SectionGroup title={t("campaign_report_section_product")} accent="#FB5B14">
                    {products.length > 1 && (
                        <ProductSelector products={products} value={selectedProductId} onChange={selectProduct} />
                    )}
                    <ReportOutcome productResults={productResults} />
                    <ReportEvaluation
                        campaign={campaign}
                        productId={selectedProductId}
                        productResults={productResults}
                    />
                    <ReportFeedback reviews={feedbackReviews} isLoading={isLoadingReviews} forceExpand={isExporting} />
                </SectionGroup>
            </Stack>
        </Box>
    );
};
